import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Space, Button, ConfigProvider, Spin } from 'antd';
import { SmileOutlined, MehOutlined, FrownOutlined } from '@ant-design/icons';
import NPSTable from './components/NPSTable';
import NPSCharts from './components/NPSCharts';
import {
  fetchNpsToday,
  fetchNpsYesterday,
  fetchNpsByWeekdays,
  fetchNpsByDateRange,
  fetchLastNps
} from './utils/fetch';
import CustomRangePicker from './components/CustomRangePicker';
import moment from 'moment';
import 'moment/locale/ru';
import ruRU from 'antd/es/locale/ru_RU';
import { changePageTitle } from "../../utils/title";

moment.locale('ru');

const NPSPage = () => {
  useEffect(() => {
    changePageTitle("NPS");
  }, []);

  const [npsData, setNpsData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [selectedChart, setSelectedChart] = useState('today');
  const [selectedDates, setSelectedDates] = useState([]);

  // Статистические данные
  const [npsToday, setNpsToday] = useState(null);
  const [npsYesterday, setNpsYesterday] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Загрузка последних NPS оценок
  useEffect(() => {
    loadLastNps();
  }, []);

  // Загрузка данных при смене типа графика
  useEffect(() => {
    loadChartData();
  }, [selectedChart, selectedDates]);

  const loadLastNps = async () => {
    setIsLoadingTable(true);
    try {
      const data = await fetchLastNps(100);
      setNpsData(data || []);
    } catch (error) {
      console.error('Error loading NPS data:', error);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const loadChartData = async () => {
    setIsLoadingStats(true);
    try {
      switch (selectedChart) {
        case 'today':
          const todayData = await fetchNpsToday();
          setNpsToday(todayData);
          break;
        case 'yesterday':
          const yesterdayData = await fetchNpsYesterday();
          setNpsYesterday(yesterdayData);
          break;
        case 'week':
          const weekData = await fetchNpsByWeekdays();
          setChartData(weekData || []);
          break;
        case 'period':
          if (selectedDates.length === 2) {
            const from = selectedDates[0].format('YYYY-MM-DD');
            const to = selectedDates[1].format('YYYY-MM-DD');
            const periodData = await fetchNpsByDateRange(from, to);
            setChartData(periodData || []);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleDateChange = (dates) => {
    setSelectedDates(dates || []);
  };

  const getNpsIcon = (score) => {
    if (score >= 4) return <SmileOutlined style={{ color: '#52c41a' }} />;
    if (score >= 3) return <MehOutlined style={{ color: '#faad14' }} />;
    return <FrownOutlined style={{ color: '#ff4d4f' }} />;
  };

  return (
    <ConfigProvider locale={ruRU}>
      <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
        <Card bordered={false} style={{ marginBottom: '24px', borderRadius: '8px' }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600 }}>Оценка NPS</h1>
        </Card>

        {/* Кнопки выбора периода */}
        <Card bordered={false} style={{ marginBottom: '24px', borderRadius: '8px' }}>
          <Space wrap size="middle">
            <Button
              type={selectedChart === 'today' ? 'primary' : 'default'}
              onClick={() => setSelectedChart('today')}
              size="large"
            >
              Сегодня
            </Button>
            <Button
              type={selectedChart === 'yesterday' ? 'primary' : 'default'}
              onClick={() => setSelectedChart('yesterday')}
              size="large"
            >
              Вчера
            </Button>
            <Button
              type={selectedChart === 'period' ? 'primary' : 'default'}
              onClick={() => setSelectedChart('period')}
              size="large"
            >
              Период
            </Button>
            {selectedChart === 'period' && (
              <CustomRangePicker onChange={handleDateChange} value={selectedDates} />
            )}
          </Space>
        </Card>

        {/* Статистика и графики */}
        <Spin spinning={isLoadingStats}>
          {selectedChart === 'today' && npsToday && (
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: '8px' }}>
                  <Statistic
                    title="Средний NPS за сегодня"
                    value={npsToday.averageScore || 0}
                    precision={1}
                    prefix={getNpsIcon(npsToday.averageScore || 0)}
                    suffix="/ 5"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: '8px' }}>
                  <Statistic
                    title="Количество оценок"
                    value={npsToday.count || 0}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: '8px' }}>
                  <Statistic
                    title="Максимальная оценка"
                    value={npsToday.maxScore || 0}
                    suffix="/ 5"
                  />
                </Card>
              </Col>
            </Row>
          )}

          {selectedChart === 'yesterday' && npsYesterday && (
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: '8px' }}>
                  <Statistic
                    title="Средний NPS за вчера"
                    value={npsYesterday.averageScore || 0}
                    precision={1}
                    prefix={getNpsIcon(npsYesterday.averageScore || 0)}
                    suffix="/ 5"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: '8px' }}>
                  <Statistic
                    title="Количество оценок"
                    value={npsYesterday.count || 0}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: '8px' }}>
                  <Statistic
                    title="Максимальная оценка"
                    value={npsYesterday.maxScore || 0}
                    suffix="/ 5"
                  />
                </Card>
              </Col>
            </Row>
          )}

          {(selectedChart === 'week' || selectedChart === 'period') && (
            <NPSCharts
              selectedChart={selectedChart}
              selectedDates={selectedDates}
              chartData={chartData}
            />
          )}
        </Spin>

        {/* Таблица последних оценок */}
        <Card
          bordered={false}
          style={{ borderRadius: '8px' }}
          title={<h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Последние оценки</h2>}
        >
          <NPSTable
            data={npsData}
            isLoading={isLoadingTable}
          />
        </Card>
      </div>
    </ConfigProvider>
  );
};

export default NPSPage;
